"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio";

import { cn } from "@/lib/utils";
import { useScopedI18n } from "@/messages/client";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import {
  CategoryTree,
  useApplicationCategory,
  useNewsCategory,
  useProductCategory,
  useProductTechDoc,
} from "./search-filter";
import { Checkbox } from "@/components/ui/checkbox";
import { Filter } from "./search-content";
import { SearchConfig } from "@/server/meilisearch";

interface CategoryItemData {
  title: string;
  count: number;
  value?: "techDoc" | "appNote" | "evmGuide";
  level?: number;
  children?: CategoryItemData[];
}

interface CategoryItemProps {
  mobile?: boolean;
  checkbox?: boolean;
  title?: string;
  data?: CategoryItemData[];
  value: "news" | "application" | "product" | "techDoc";
  loading?: boolean;
  filter?: Filter[];
  onChange?: Dispatch<SetStateAction<Filter[]>>;
  hide?: boolean;
}

const findNodeByname = (
  tree: CategoryItemData[],
  name: string
): CategoryItemData | null => {
  for (const node of tree) {
    if (node.title === name) {
      return node;
    }
    if (node.children && node.children.length > 0) {
      const foundNode = findNodeByname(node.children, name);
      if (foundNode) {
        return foundNode;
      }
    }
  }
  return null;
};

function CategoryItemSkeleton() {
  return (
    <div className="flex flex-col space-y-2 py-2.5">
      <div className="h-5 w-full animate-pulse bg-slate-200" />
      <div className="h-5 w-full animate-pulse bg-slate-200" />
      <div className="h-5 w-full animate-pulse bg-slate-200" />
    </div>
  );
}

function CategoryItemCheckbox(props: CategoryItemProps) {
  return (
    <div className="space-y-2">
      {props.data?.map((item, index) => (
        <div
          key={index}
          className="flex items-center justify-between text-sm text-[#383838]">
          <label className="flex items-center space-x-2">
            <Checkbox
              checked={!!props.filter?.find((i) => i.value === item.value)}
              onCheckedChange={(checked) => {
                console.log(checked, "checked");

                const data = props.filter?.find((i) => i.value === item.value);

                if (checked) {
                  props.onChange?.((prev) => [
                    ...prev,
                    {
                      type: props.value,
                      key: item.value ?? "techDoc",
                      label: [item.title ?? ""],
                      value: item.value ?? "techDoc",
                      level: item?.level,
                    },
                  ]);
                } else {
                  props.onChange?.((prev) =>
                    prev.filter((i) => i.key !== data?.key)
                  );
                }
              }}
            />

            <span className="select-none">{item.title}</span>
          </label>

          <span>（{item.count}）</span>
        </div>
      ))}
    </div>
  );
}

function CategoryItemSelect(props: CategoryItemProps) {
  const t = useScopedI18n("全局搜索");

  const [category, setCategory] = useState<CategoryItemData[]>([]);

  const [current, setCurrent] = useState<CategoryItemData | null>(null);

  useEffect(() => {
    if (!props.filter?.length) {
      setCategory([]);
      setCurrent(null);
    } else {
      const data = props.filter.find((i) => i.key === props.value);

      if (!data) {
        setCategory([]);
        setCurrent(null);
      } else {
        const nodes = data.label
          .map((i) => findNodeByname(props.data ?? [], i))
          .filter((i) => i !== null);

        const category = nodes.slice(0, nodes.length - 1);
        setCategory(category as CategoryItemData[]);
        setCurrent(nodes[nodes.length - 1]);
      }
    }
  }, [props]);

  return (
    <>
      {current && (
        <>
          <div
            onClick={() => {
              setCurrent(null);
              setCategory([]);
              props.onChange?.((prv) =>
                prv.filter((i) => i.key !== props.value)
              );
            }}
            className={cn(
              "mb-2 flex cursor-pointer select-none items-center space-x-1 text-sm underline-offset-4 hover:underline",
              {
                "ml-5": props.mobile,
              }
            )}>
            {props.mobile ? (
              <ChevronLeft size={18} />
            ) : (
              <ChevronLeft size={12} />
            )}

            <span>{t("所有类别")}</span>
          </div>

          {category?.map((item, index) => (
            <div
              onClick={() => {
                setCurrent(item);
                setCategory((prev) => prev.filter((_, i) => i !== index));

                props.onChange?.((prev) => {
                  const data = prev.find((i) => i.key === props.value);

                  const index = data?.label?.findIndex((i) => i === item.title);

                  return prev.map((i) =>
                    i.key === props.value
                      ? {
                          ...i,
                          label: [
                            ...(data?.label ?? []).slice(0, index),
                            item.title,
                          ],
                          level: item?.level || 1,
                          value: item.title,
                        }
                      : i
                  );
                });
              }}
              key={index}
              className={cn(
                "mb-2 flex cursor-pointer select-none items-center space-x-1 text-sm underline-offset-4 hover:underline",
                {
                  "ml-5": props.mobile,
                }
              )}>
              {props.mobile ? (
                <ChevronLeft size={18} />
              ) : (
                <ChevronLeft size={12} />
              )}

              <span>{item.title}</span>
            </div>
          ))}
        </>
      )}

      {current && (
        <div className={cn({ "ml-5": props.mobile })}>
          <div
            className={cn(
              "mb-2 flex cursor-pointer select-none items-center pl-4 text-sm text-[#383838] underline-offset-4 hover:underline",
              {
                "text-base font-medium":
                  current.children && current.children?.length > 0,
                "pl-6": props.mobile,
              }
            )}>
            <span>{current.title}</span>
          </div>

          <div className="space-y-2 pl-4">
            {current.children?.map((item, index) => (
              <div
                onClick={() => {
                  setCategory((prev) => [...prev, current]);
                  setCurrent(item);

                  props.onChange?.((prev) => {
                    const data = prev.find((i) => i.type === props.value);

                    return prev.map((i) =>
                      i.type === props.value
                        ? {
                            ...i,
                            label: [...(data?.label ?? []), item.title],
                            level: item.level ?? 1,
                            value: item.title,
                          }
                        : i
                    );
                  });
                }}
                key={index}
                className={cn(
                  "flex cursor-pointer select-none items-center justify-between pl-3 text-sm text-[#383838] transition-colors duration-300 hover:text-primary",
                  {
                    "pl-6": props.mobile,
                  }
                )}>
                <div className="flex items-center space-x-3">
                  {props.mobile && (
                    <RadioGroup>
                      <RadioGroupItem value="correlation" />
                    </RadioGroup>
                  )}
                  <span>{item.title}</span>
                </div>

                <span className="lg:block">（{item.count}）</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!current && (
        <div className="space-y-2">
          {props.data?.map((item, index) => (
            <div
              key={index}
              onClick={() => {
                setCurrent(item);

                props.onChange?.((prev) => [
                  ...prev,
                  {
                    value: item.title,
                    label: [item.title],
                    key: props.value,
                    type: props.value,
                    level: 1,
                  },
                ]);
              }}
              className={cn(
                "flex cursor-pointer select-none items-center justify-between pl-3 text-sm text-[#383838] transition-colors duration-300 hover:text-primary",
                {
                  "pl-5": props.mobile,
                }
              )}>
              <div className="flex items-center space-x-3">
                {props.mobile && (
                  <RadioGroup>
                    <RadioGroupItem value="correlation" />
                  </RadioGroup>
                )}

                <span>{item.title}</span>
              </div>

              <div className="flex items-center">
                <span className="hidden lg:block">（{item.count}）</span>

                {item.children && item.children?.length > 0 && (
                  <span className="pr-0.5 lg:hidden">
                    <ChevronRight size={16} />
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function CategoryItemContent(props: CategoryItemProps) {
  return (
    <div className="flex flex-col py-2.5">
      {props.checkbox ? (
        <CategoryItemCheckbox {...props} />
      ) : (
        <CategoryItemSelect {...props} />
      )}
    </div>
  );
}

function CategoryItem(props: CategoryItemProps) {
  return (
    <Accordion
      type="single"
      collapsible
      className="lg:rounded-lg lg:border lg:border-[#E5E5E5] lg:px-4"
      value={props.mobile ? undefined : props.value}>
      <AccordionItem className="border-none" value={props.value}>
        <AccordionTrigger
          showIcon={!!props.mobile}
          className="border-b border-[#2C2C2C] py-0 pb-1 pr-1 pt-3 outline-none lg:border-[#A6A6A6]">
          <div className="flex items-center space-x-3">
            {props.mobile && (
              <RadioGroup>
                <RadioGroupItem
                  checked={
                    props.filter?.findIndex((i) => i.key === props.value) !== -1
                  }
                  value={props.value}
                />
              </RadioGroup>
            )}

            <div className="text-base font-medium">{props.title}</div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="border-none">
          {props.loading ? (
            <CategoryItemSkeleton />
          ) : (
            <CategoryItemContent {...props} />
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function CategoryMobile(props: {
  filter?: Filter[];
  setFilter: Dispatch<SetStateAction<Filter[]>>;
  sort: "correlation" | "date";
  setSort: Dispatch<SetStateAction<"correlation" | "date">>;
  productData?: CategoryTree[];
  applicationData?: CategoryTree[];
  techDocData?: {
    techDoc: {
      level: number;
      value: number;
    };
    appNote: number;
    evmGuide: number;
  };
  newsData?: {
    title: string;
    count: number;
    id: number;
  }[];
  isProduct?: boolean;
  isApplication?: boolean;
  isNews?: boolean;
}) {
  const t = useScopedI18n("全局搜索");

  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div className="flex h-8 cursor-pointer items-center justify-center rounded-2xl border border-[#E5E5E5] text-sm text-[#2C2C2C] transition-colors duration-300 hover:border-primary hover:text-primary">
          {t("更多筛选条件")}（{props.filter?.length ?? 0}）
        </div>
      </DialogTrigger>
      <DialogContent className="h-screen w-screen max-w-none border-none bg-white p-0 shadow-none">
        <div className="flex h-full w-full flex-col">
          <div className="relative flex h-[3.75rem] flex-shrink-0 items-center justify-center border-b border-[#E5E5E5] text-2xl text-[#2C2C2C]">
            <span>{t("筛选条件")}</span>

            <DialogClose asChild>
              <div className="absolute right-6 top-1/2 -translate-y-1/2 cursor-pointer text-[#E5E5E5] transition-colors duration-300 hover:text-primary">
                <X size={30} />
              </div>
            </DialogClose>
          </div>

          <div className="flex h-[calc(100vh-260px)] flex-col overflow-hidden pt-4">
            <div className="flex-shrink-0 px-6">
              <div className="flex h-11  items-center justify-between border-b border-[#E5E5E5]">
                <span className="text-xl font-medium text-[#2C2C2C]">
                  {t("类别")}
                </span>

                <span
                  className="text-sm font-medium text-primary"
                  onClick={() => {
                    props.setFilter([]);
                    props.setSort("correlation");
                  }}>
                  {t("重置")}
                </span>
              </div>
            </div>

            <div className="flex-1 space-y-8 overflow-y-auto px-6 pt-4">
              {props.isProduct && (
                <CategoryItem
                  title={t("产品")}
                  value="product"
                  mobile
                  data={props.productData}
                  filter={props.filter}
                  onChange={props.setFilter}
                />
              )}

              {props.isApplication && (
                <CategoryItem
                  value="application"
                  title={t("应用")}
                  mobile
                  data={props.applicationData ?? []}
                  filter={props.filter}
                  onChange={props.setFilter}
                />
              )}

              {props.isProduct && (
                <CategoryItem
                  value="techDoc"
                  title={t("技术文档")}
                  checkbox
                  mobile
                  data={[
                    {
                      value: "techDoc",
                      title: t("产品数据手册"),
                      level: props.techDocData?.techDoc?.level,
                      count: props.techDocData?.techDoc?.value ?? 0,
                    },
                    {
                      value: "appNote",
                      title: t("产品应用手册"),
                      count: props.techDocData?.appNote ?? 0,
                    },
                    {
                      value: "evmGuide",
                      title: t("EVM用户指南"),
                      count: props.techDocData?.evmGuide ?? 0,
                    },
                  ]}
                  filter={props.filter}
                  onChange={props.setFilter}
                />
              )}

              {props.isNews && (
                <CategoryItem
                  mobile
                  value="news"
                  title={t("新闻")}
                  data={props.newsData ?? []}
                  filter={props.filter}
                  onChange={props.setFilter}
                />
              )}
            </div>
          </div>

          <div className="flex-shrink-0 px-6 pb-7">
            <div className="pb-4">
              <div className="flex h-11 items-center border-b border-[#E5E5E5] text-xl font-medium text-[#2C2C2C]">
                <span>{t("排序")}</span>
              </div>

              <RadioGroup
                value={props.sort}
                onValueChange={(value) => {
                  props.setSort(value as "correlation" | "date");
                }}
                className="py-4">
                <label className="flex items-center space-x-3 text-sm font-medium text-[#2C2C2C]">
                  <RadioGroupItem value="correlation" />

                  <span>{t("相关性")}</span>
                </label>

                <label className="flex items-center space-x-3 text-sm font-medium text-[#2C2C2C]">
                  <RadioGroupItem value="date" />

                  <span>{t("日期")}</span>
                </label>
              </RadioGroup>
            </div>

            <Button
              className="h-11 w-full rounded-md"
              onClick={() => {
                setOpen(false);
              }}>
              {t("确定")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Category({
  site,
  lang,
  keyword,
  total,
  filter,
  setFilter,
  sort,
  setSort,
  searchConfig
}: {
  site: number;
  lang: number;
  keyword: string | null;
  total: number;
  filter?: Filter[];
  setFilter: Dispatch<SetStateAction<Filter[]>>;
  sort: "correlation" | "date";
  setSort: Dispatch<SetStateAction<"correlation" | "date">>;
  searchConfig: SearchConfig;
}) {
  const t = useScopedI18n("全局搜索");

  const { data: productData, isLoading: isProductLoading } = useProductCategory(
    {
      site: site,
      lang: lang,
      keyword: keyword || "",
      filter: filter,
      searchConfig
    }
  );

  const { data: techDocData, isLoading: isTechDocLoading } = useProductTechDoc({
    site: site,
    lang: lang,
    keyword: keyword || "",
    filter: filter,
    searchConfig
  });

  const { data: applicationData, isLoading: isApplicationLoading } =
    useApplicationCategory({
      site: site,
      lang: lang,
      keyword: keyword || "",
      filter: filter,
      searchConfig
    });

  const { data: newsData, isLoading: isNewsLoading } = useNewsCategory({
    site: site,
    lang: lang,
    keyword: keyword || "",
    searchConfig
  });

  const isProduct = useMemo(() => {
    const application = filter?.find((i) => i.key === "application");
    return (
      !filter?.find((i) => i.key === "news") && (application?.level ?? 1) === 1
    );
  }, [filter]);

  const isApplication = useMemo(() => {
    const product = filter?.find((i) => i.key === "product");
    return (
      !filter?.find((i) => i.key === "news") && (product?.level ?? 1) === 1
    );
  }, [filter]);

  const isNews = useMemo(() => {
    return !filter?.length || !filter?.findIndex((i) => i.key === "news");
  }, [filter]);

  //   if (!props.filter?.length) {
  //     setIsProduct(true);
  //     setIsApplication(true);
  //     setIsNews(true);
  //   } else {
  //     const application = props.filter?.find((i) => i.key === "application");
  //     const product = props.filter?.find((i) => i.key === "product");

  //     setIsProduct(
  //       !props.filter?.find((i) => i.key === "news") &&
  //         (application?.level ?? 1) === 1
  //     );

  //     setIsApplication(
  //       !props.filter?.find((i) => i.key === "news") &&
  //         (product?.level ?? 1) === 1
  //     );

  //     setIsNews(
  //       !props.filter?.length ||
  //         !props.filter?.findIndex((i) => i.key === "news")
  //     );
  //   }
  // }, [props.filter]);

  useEffect(() => {
    if (!isProduct) {
      setFilter((prev) =>
        prev.filter(
          (i) =>
            i.key !== "product" &&
            i.key !== "techDoc" &&
            i.key !== "appNote" &&
            i.key !== "evmGuide"
        )
      );
    }
  }, [isProduct, setFilter]);

  useEffect(() => {
    if (!isApplication) {
      setFilter((prev) => prev.filter((i) => i.key !== "application"));
    }
  }, [isApplication, setFilter]);

  useEffect(() => {
    if (!isNews) {
      setFilter((prev) => prev.filter((i) => i.key !== "news"));
    }
  }, [isNews, setFilter]);

  return (
    <>
      <div className="hidden w-60 flex-shrink-0 lg:block">
        <div className="flex items-center justify-between border-b border-[#A6A6A6] pb-1">
          <div className="text-xl font-medium">{t("类别")}</div>

          <button
            className="text-sm text-primary transition-colors duration-300 hover:text-primary/80"
            onClick={() => {
              setFilter([]);
              setSort("correlation");
            }}>
            {t("重置")}
          </button>
        </div>

        <div className="flex flex-col">
          <RadioGroup className="flex items-center py-6">
            <label className="flex items-center space-x-3 text-base text-[#383838]">
              <RadioGroupItem checked value="all" />
              <div className="flex items-center text-base text-[#383838]">
                <span>{t("全部")}</span>
                <span>（{total}）</span>
              </div>
            </label>
          </RadioGroup>

          <div className="flex flex-col gap-6">
            {isProduct && (
              <CategoryItem
                loading={isProductLoading}
                value="product"
                title={t("产品")}
                data={productData ?? []}
                filter={filter}
                onChange={setFilter}
              />
            )}

            {isApplication && (
              <CategoryItem
                loading={isApplicationLoading}
                value="application"
                title={t("应用")}
                data={applicationData ?? []}
                filter={filter}
                onChange={setFilter}
              />
            )}

            {isProduct && (
              <CategoryItem
                loading={isTechDocLoading}
                value="techDoc"
                title={t("技术文档")}
                checkbox
                data={[
                  {
                    value: "techDoc",
                    title: t("产品数据手册"),
                    level: techDocData?.techDoc?.level,
                    count: techDocData?.techDoc?.value ?? 0,
                  },
                  {
                    value: "appNote",
                    title: t("产品应用手册"),
                    count: techDocData?.appNote ?? 0,
                  },
                  {
                    value: "evmGuide",
                    title: t("EVM用户指南"),
                    count: techDocData?.evmGuide ?? 0,
                  },
                ]}
                filter={filter}
                onChange={setFilter}
              />
            )}

            {isNews && (
              <CategoryItem
                loading={isNewsLoading}
                value="news"
                title={t("新闻")}
                data={newsData ?? []}
                filter={filter}
                onChange={setFilter}
              />
            )}
          </div>
        </div>

        <div className="pt-20">
          <div className="flex items-center justify-between border-b border-[#A6A6A6] pb-1">
            <div className="text-xl font-medium">{t("排序")}</div>
          </div>

          <RadioGroup
            value={sort}
            onValueChange={(value) => {
              setSort(value as "correlation" | "date");
            }}
            className="py-6">
            <label className="flex cursor-pointer items-center space-x-4 text-base text-[#383838]">
              <RadioGroupItem value="correlation" />

              <span>{t("相关性")}</span>
            </label>

            <label className="flex cursor-pointer items-center space-x-4 text-base text-[#383838]">
              <RadioGroupItem value="date" />

              <span>{t("日期")}</span>
            </label>
          </RadioGroup>
        </div>
      </div>

      <div className="lg:hidden">
        <CategoryMobile
          filter={filter}
          setFilter={setFilter}
          sort={sort}
          setSort={setSort}
          productData={productData}
          applicationData={applicationData}
          techDocData={techDocData}
          newsData={newsData}
          isProduct={isProduct}
          isApplication={isApplication}
          isNews={isNews}
        />
      </div>
    </>
  );
}
